import React, { useEffect, useState } from "react";
import logo from "../assets/logo_nobg.png";

const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID;

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
}

export default function Studio() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    async function fetchVideos() {
      try {
        const res = await fetch(
          `https://www.googleapis.com/youtube/v3/search?key=${API_KEY}&channelId=${CHANNEL_ID}&part=snippet,id&order=date&maxResults=20&type=video`
        );
        if (!res.ok) throw new Error("YouTube API error");
        const data = await res.json();
        setVideos(data.items || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchVideos();
  }, []);

  const featured = videos[0];
  const rest = videos.slice(1);

  return (
    <div className="st">
      <style>{CSS}</style>

      <nav className="st-nav">
        <div className="st-nav-inner">
          <a href="https://shashankupadhyaya.com" className="st-logo">
            <img src={logo} alt="S/U" className="site-logo-img" />
            <span className="st-logo-label">Studio</span>
          </a>
          <a href="https://www.youtube.com/@shaxhxnk" target="_blank" rel="noreferrer" className="st-btn st-btn--outline">
            YouTube Channel ↗
          </a>
          <a href="https://shashankupadhyaya.com" className="st-nav-back">← Portfolio</a>
        </div>
      </nav>

      <div className="st-page">

        <div className="st-header">
          <div className="st-header-inner">
            <span className="st-chip">Music &amp; Production</span>
            <h1 className="st-h1">Studio &amp; Releases</h1>
            <p className="st-lead">Original productions, compositions, and video content from Shashank Upadhyaya.</p>
          </div>
        </div>

        <div className="st-container">

          {loading && (
            <div className="st-state">
              <div className="st-spinner" />
              <span>Loading videos…</span>
            </div>
          )}

          {error && (
            <div className="st-state st-state--err">
              <span className="st-state-icon">⚠</span>
              <span>Could not load videos. Check your YouTube API key.</span>
            </div>
          )}

          {!loading && !error && featured && (
            <>
              {/* Featured / active video player */}
              <div className="st-section">
                <h2 className="st-section-title">
                  {activeVideo ? "Now Playing" : "Latest Upload"}
                </h2>
                <div className="st-player-wrap">
                  <div className="st-player">
                    <iframe
                      src={`https://www.youtube.com/embed/${(activeVideo || featured).id.videoId}?autoplay=${activeVideo ? 1 : 0}&rel=0`}
                      title={(activeVideo || featured).snippet.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="st-player-info">
                    <h2 className="st-player-title">{(activeVideo || featured).snippet.title}</h2>
                    <p className="st-player-date">{formatDate((activeVideo || featured).snippet.publishedAt)}</p>
                    <p className="st-player-desc">{(activeVideo || featured).snippet.description?.slice(0, 180)}{(activeVideo || featured).snippet.description?.length > 180 ? "…" : ""}</p>
                    <a
                      href={`https://www.youtube.com/watch?v=${(activeVideo || featured).id.videoId}`}
                      target="_blank" rel="noreferrer"
                      className="st-btn st-btn--gold"
                    >
                      Watch on YouTube ↗
                    </a>
                  </div>
                </div>
              </div>

              {/* Video grid */}
              {rest.length > 0 && (
                <div className="st-section">
                  <h2 className="st-section-title">More Videos</h2>
                  <div className="st-grid">
                    {rest.map((v) => (
                      <div
                        key={v.id.videoId}
                        className={`st-card${activeVideo?.id.videoId === v.id.videoId ? " st-card--active" : ""}`}
                        onClick={() => { setActiveVideo(v); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      >
                        <div className="st-thumb">
                          <img
                            src={v.snippet.thumbnails?.medium?.url || v.snippet.thumbnails?.default?.url}
                            alt={v.snippet.title}
                          />
                          <div className="st-play-icon">▶</div>
                        </div>
                        <div className="st-card-body">
                          <h3 className="st-card-title">{v.snippet.title}</h3>
                          <span className="st-card-date">{formatDate(v.snippet.publishedAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {!loading && !error && videos.length === 0 && (
            <div className="st-state">
              <span className="st-state-icon">🎵</span>
              <p>No videos yet. Check back soon.</p>
            </div>
          )}

        </div>

        <footer className="st-footer">
          <div className="st-container">
            <span>© 2026 Shashank Upadhyaya</span>
            <a href="https://shashankupadhyaya.com">shashankupadhyaya.com ↗</a>
          </div>
        </footer>
      </div>
    </div>
  );
}

const CSS = `
  .st {
    --bg: #0A0B0D;
    --card: #0E1116;
    --border: rgba(255,255,255,0.08);
    --text: #EDEFF2;
    --muted: #9AA3AF;
    --gold: #C7F94B;
    --gold-dim: rgba(199,249,75,0.12);
    --nav-h: 64px;
    --radius: 8px;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    font-size: 15px;
    -webkit-font-smoothing: antialiased;
  }
  .st *, .st *::before, .st *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .st a { color: inherit; text-decoration: none; }
  .st button { cursor: pointer; font-family: inherit; }

  /* Nav */
  .st-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 50;
    height: var(--nav-h); background: rgba(10,11,13,0.95);
    backdrop-filter: blur(20px); border-bottom: 1px solid var(--border);
  }
  .st-nav-inner {
    max-width: 1100px; margin: 0 auto; padding: 0 28px;
    height: 100%; display: flex; align-items: center; gap: 20px;
  }
  .st-logo { display: flex; align-items: center; gap: 10px; }
  .site-logo-img { height: 56px; width: auto; display: block; object-fit: contain; }
  .st-logo-label { font-size: 13px; color: var(--muted); padding-left: 10px; border-left: 1px solid var(--border); }
  .st-nav-back { font-size: 13px; color: var(--muted); margin-left: auto; transition: color 0.15s; }
  .st-nav-back:hover { color: var(--gold); }

  /* Page */
  .st-page { padding-top: var(--nav-h); }

  /* Header */
  .st-header { border-bottom: 1px solid var(--border); padding: 64px 0 48px; }
  .st-header-inner { max-width: 1100px; margin: 0 auto; padding: 0 28px; }
  .st-chip {
    display: inline-block; font-size: 11px; font-weight: 600; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--gold);
    background: var(--gold-dim); border: 1px solid rgba(199,249,75,0.25);
    padding: 4px 10px; border-radius: 4px; margin-bottom: 20px;
  }
  .st-h1 {
    font-family: 'Archivo', sans-serif; font-weight: 900;
    font-size: clamp(32px, 5vw, 52px); letter-spacing: -0.03em; line-height: 1.05; margin-bottom: 14px;
  }
  .st-lead { font-size: 16px; color: var(--muted); max-width: 480px; line-height: 1.7; }

  /* Container */
  .st-container { max-width: 1100px; margin: 0 auto; padding: 0 28px; }

  /* Section */
  .st-section { padding: 48px 0; border-bottom: 1px solid var(--border); }
  .st-section-title {
    font-family: 'Archivo', sans-serif; font-weight: 700;
    font-size: 13px; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--muted); margin-bottom: 24px;
  }

  /* Player */
  .st-player-wrap { display: grid; grid-template-columns: 1fr 320px; gap: 32px; align-items: start; }
  .st-player { position: relative; padding-bottom: 56.25%; border-radius: var(--radius); overflow: hidden; background: #000; }
  .st-player iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
  .st-player-info { display: flex; flex-direction: column; gap: 12px; }
  .st-player-title { font-family: 'Archivo', sans-serif; font-weight: 700; font-size: 18px; line-height: 1.3; }
  .st-player-date { font-size: 13px; color: var(--muted); }
  .st-player-desc { font-size: 13px; color: var(--muted); line-height: 1.7; }

  /* Grid */
  .st-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  .st-card {
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--radius); overflow: hidden; cursor: pointer;
    transition: border-color 0.2s, transform 0.2s;
  }
  .st-card:hover { border-color: var(--gold); transform: translateY(-2px); }
  .st-card--active { border-color: var(--gold); }
  .st-thumb { position: relative; aspect-ratio: 16/9; background: #2e2e2e; overflow: hidden; }
  .st-thumb img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
  .st-card:hover .st-thumb img { transform: scale(1.04); }
  .st-play-icon {
    position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    font-size: 24px; color: white; opacity: 0; background: rgba(0,0,0,0.4);
    transition: opacity 0.2s;
  }
  .st-card:hover .st-play-icon { opacity: 1; }
  .st-card-body { padding: 14px; }
  .st-card-title {
    font-family: 'Archivo', sans-serif; font-weight: 600; font-size: 13px;
    line-height: 1.4; margin-bottom: 6px;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .st-card-date { font-size: 12px; color: var(--muted); }

  /* Buttons */
  .st-btn {
    display: inline-flex; align-items: center; font-size: 14px; font-weight: 600;
    padding: 10px 20px; border-radius: 6px; border: none;
    cursor: pointer; transition: all 0.15s; white-space: nowrap;
  }
  .st-btn--gold { background: var(--gold); color: #000; }
  .st-btn--gold:hover { opacity: 0.88; }
  .st-btn--outline {
    background: transparent; color: var(--text); border: 1px solid var(--border);
    margin-left: auto;
  }
  .st-btn--outline:hover { border-color: var(--gold); color: var(--gold); }

  /* States */
  .st-state {
    text-align: center; padding: 80px 0;
    display: flex; flex-direction: column; align-items: center; gap: 14px;
    font-size: 14px; color: var(--muted);
  }
  .st-state--err { color: rgba(239,68,68,0.7); }
  .st-state-icon { font-size: 32px; }
  .st-spinner {
    width: 28px; height: 28px; border-radius: 50%;
    border: 2px solid var(--border); border-top-color: var(--gold);
    animation: st-spin 0.75s linear infinite;
  }
  @keyframes st-spin { to { transform: rotate(360deg); } }

  /* Footer */
  .st-footer { margin-top: 80px; border-top: 1px solid var(--border); padding: 28px 0; }
  .st-footer .st-container { display: flex; align-items: center; justify-content: space-between; font-size: 13px; color: var(--muted); }
  .st-footer a:hover { color: var(--gold); }

  /* Responsive */
  @media (max-width: 1000px) {
    .st-grid { grid-template-columns: repeat(3, 1fr); }
    .st-player-wrap { grid-template-columns: 1fr; }
    .st-player-info { max-width: 640px; }
  }
  @media (max-width: 700px) {
    .st-grid { grid-template-columns: repeat(2, 1fr); }
    .st-h1 { font-size: 28px; }
    .st-btn--outline { display: none; }
    .st-header { padding: 40px 0 28px; }
    .st-logo-label { display: none; }
    .site-logo-img { height: 40px; }
    .st-container, .st-header-inner { padding-left: 16px; padding-right: 16px; }
  }
  @media (max-width: 480px) {
    .st-grid { grid-template-columns: 1fr; }
    .st-section { padding: 32px 0; }
    .st-nav-back { display: none; }
    .st-footer .st-container { flex-direction: column; gap: 8px; text-align: center; }
  }
`;
